// ─── Per-key notes for the settings tab (V9-p) ────────────────────────────────
//
// The registry's own `description` (backend-nest settings.registry.ts) says
// what a key *is* — one English sentence, written for whoever reads the API.
// A few keys need more than that in the panel: what actually happens while
// the switch is on, what turning it off sets in motion, and — for a sensitive
// key — what the admin is about to cause the moment they confirm. That is a
// panel concern (Persian, operational, aimed at the person holding the
// switch), not a registry one, so it lives here instead of being bolted onto
// the API.
//
// The tab stays generic: it renders every registered key the same way and
// simply asks this module whether that key has anything extra to say.

/**
 * A note is `{ title, points[], confirm? }`.
 *
 * `confirm` is keyed by the editor's text for the value about to be saved
 * ('true' / 'false' for a boolean), so a sensitive switch can say something
 * different on the way on than on the way off — which for `planB.enabled` is
 * the whole point: turning it *off* is the half that drains the queue.
 */
const NOTES = {
  'planB.enabled': {
    title: 'این کلید دقیقاً چه می‌کند؟',
    points: [
      'تا وقتی روشن است، ضبط‌های کاربرانی که پلن ب را پذیرفته‌اند بی‌درنگ در «صف بررسی» می‌نشینند و اصلاً به Azure فرستاده نمی‌شوند.',
      'کار خودکارِ تحلیل دوباره هم تا وقتی این کلید روشن است به صف دست نمی‌زند؛ صف فقط با امتیاز دستی شما در تب «صف بررسی» کوتاه می‌شود.',
      'خاموش‌کردن همین کلید است که صف را خالی می‌کند: از اولین اجرای بعدیِ کار خودکار، تلاش‌های در صف دوباره به Azure سپرده می‌شوند.',
      'کاربری که پلن ب را نپذیرفته باشد، در هر دو حالت مثل همیشه کار می‌کند.',
    ],
    confirm: {
      true: 'با تأیید، از همین لحظه ضبط‌های تازه به‌جای Azure در صف بررسی می‌نشینند و تا خاموش‌شدن این کلید هیچ تحلیل خودکاری انجام نمی‌شود.',
      false: 'با تأیید، کار خودکار از اجرای بعدی تلاش‌های در صف را دوباره به Azure می‌سپارد و صف کم‌کم خالی می‌شود.',
    },
  },
  'planB.': {
    title: 'مربوط به صف پلن ب',
    points: [
      'این کلید فقط زمان‌بندی و اندازهٔ کارِ خودکارِ تحلیل دوباره را تنظیم می‌کند.',
      'تا وقتی planB.enabled روشن است اثری ندارد — در آن حالت صف دست‌نخورده می‌ماند.',
    ],
  },
  'sms.smsirUnitPriceRial': {
    title: 'تعرفهٔ پیامک (مصرف پیامک)',
    points: [
      'مبلغی که ریما بابت هر واحد هزینهٔ SMS.ir از طوطی می‌گیرد، به ریال و عدد صحیح.',
      'تب «مصرف پیامک» واحدهای هزینهٔ هر ماه را در همین عدد ضرب می‌کند؛ تا خالی (null) است فقط تعداد و واحد نشان داده می‌شود.',
      'تغییرش روی همهٔ ماه‌ها اثر دارد، گذشته هم — صورت‌حساب ماهی که فرستاده شده را پیش از تغییر از تب «مصرف پیامک» با CSV نگه دارید.',
    ],
  },
}

/**
 * The note for a key: its own if it has one, otherwise the longest matching
 * prefix note (`planB.retryMaxSeconds` → the shared `planB.` note), otherwise
 * null. Prefix entries are the ones ending in a dot, so `planB.enabled` can
 * never be matched by `planB.` while it has an entry of its own.
 */
export function settingNote(key) {
  if (NOTES[key]) return NOTES[key]
  let best = null
  let bestLength = 0
  for (const [prefix, note] of Object.entries(NOTES)) {
    if (!prefix.endsWith('.')) continue
    if (key.startsWith(prefix) && prefix.length > bestLength) { best = note; bestLength = prefix.length }
  }
  return best
}

/**
 * The extra line the inline confirmation shows for the value about to be
 * saved — `text` is the editor's text, exactly what `toEditorText` produced.
 * Null when the key has no note, or none for that particular value.
 */
export function settingConfirmNote(key, text) {
  return settingNote(key)?.confirm?.[text] ?? null
}
